import enUSMessages from '@pequity/shared/locales/en-US.json';
import type { Locale } from '@pequity/shared/types/company';
import type { App, Plugin, WritableComputedRef } from 'vue';
import { createI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

type MessageSchema = typeof enUSMessages;

const DEFAULT_LOCALE = 'en-US';
const LOCALE_STORAGE_KEY = 'user_locale';

const localeModules = import.meta.glob([
  '../../../packages/shared/src/locales/*.json',
  '!../../../packages/shared/src/locales/en-US.json',
]);

const localesMap: Record<string, () => Promise<{ default: MessageSchema }>> = {};

for (const [path, loader] of Object.entries(localeModules)) {
  const locale = path.match(/([\w-]*)\.json$/)?.[1];
  if (locale) {
    localesMap[locale] = loader as () => Promise<{ default: MessageSchema }>;
  }
}

// Available locales
const availableLocales = Object.keys(localesMap);

// Type predicate function to check if a value is a valid locale
const isLocale = (value: unknown): value is Locale => {
  return typeof value === 'string' && availableLocales.includes(value);
};

// Track loaded languages to avoid reloading
const loadedLanguages: Locale[] = [DEFAULT_LOCALE];

const i18n = createI18n<[MessageSchema], typeof DEFAULT_LOCALE>({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    [DEFAULT_LOCALE]: enUSMessages,
  },
});

// When setting legacy to false, the locale property is typed as a string instead of a writable computed ref
// This is a workaround to get the proper typing
const locale = i18n.global.locale as unknown as WritableComputedRef<Locale>;

const getLocaleFromLocalStorage = () => {
  const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);

  if (isLocale(savedLocale)) {
    return savedLocale;
  }

  return null;
};

const setLocaleInLocalStorage = (localeToSet: Locale) => {
  localStorage.setItem(LOCALE_STORAGE_KEY, localeToSet);
};

// Get locale with fallback priority: user profile → local storage → company default
const getStoredLocale = () => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  const storedLocale = authStore.profile.locale ?? getLocaleFromLocalStorage() ?? companyStore.company.locale;

  return storedLocale;
};

const WAYPOINT_LOCALE_MAP: Partial<Record<Locale, string>> = {
  'sr-YU': 'sr',
  'vi-VN': 'vi',
};

const toWaypointLocale = (i18nLocale: Locale): string =>
  WAYPOINT_LOCALE_MAP[i18nLocale] ?? i18nLocale.toLowerCase().replace('-', '_');

// Set the current locale and update the HTML lang attribute
const setI18nLanguage = (localeToSet: Locale): Locale => {
  locale.value = localeToSet;

  /**
   * TODO: Set the Accept-Language header once backend supports localized responses.
   * This will ensure HTTP requests include the current locale preference so the API
   * can return localized error messages, notifications, and other content.
   *
   * Example: axios.defaults.headers.common['Accept-Language'] = locale
   */
  document.querySelector('html')?.setAttribute('lang', locale.value);

  __SYNERG_READY__
    .then((cfg) => cfg.setLocale(toWaypointLocale(localeToSet)))
    .catch(() => console.warn(`[i18n] Waypoint does not support locale "${localeToSet}"`));

  return locale.value;
};

// Load locale messages asynchronously and set the current locale
const loadLocaleMessages = async (localeToLoad: Locale | null): Promise<Locale> => {
  const localeToSet = localeToLoad || DEFAULT_LOCALE;
  const currentLocale = locale.value;

  // If the same language
  if (currentLocale === localeToSet) {
    return setI18nLanguage(localeToSet);
  }

  // If the language was already loaded
  if (loadedLanguages.includes(localeToSet)) {
    return setI18nLanguage(localeToSet);
  }

  // Skip dynamic import for default locale since it's already statically loaded
  if (localeToSet === DEFAULT_LOCALE) {
    loadedLanguages.push(localeToSet);
    return setI18nLanguage(localeToSet);
  }

  // Validate that the locale exists in the localesMap before trying to load it
  if (!localesMap[localeToSet]) {
    console.warn(`Locale "${localeToSet}" not found in localesMap. Falling back to default locale.`);
    return setI18nLanguage(DEFAULT_LOCALE);
  }

  // Load locale messages dynamically
  const messages = await localesMap[localeToSet]();
  i18n.global.setLocaleMessage(localeToSet, messages.default);
  loadedLanguages.push(localeToSet);

  return setI18nLanguage(localeToSet);
};

const I18nPlugin: Plugin = {
  install(app: App) {
    app.use(i18n);
  },
};

const tGlobal = i18n.global.t;

export { getStoredLocale, i18n, I18nPlugin, loadLocaleMessages, locale, setLocaleInLocalStorage, tGlobal };
